import { useSearchParams } from "react-router-dom";
import PageContainer from "../components/layout/PageContainer";
import LoadingSpinner from "../components/common/LoadingSpinner";
import { useApi } from "../hooks/useApi";
import { billingApi } from "../api/billing";
import { formatCurrency, formatDate } from "../utils/format";

export default function Payments() {
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status");
  const payments = useApi(() => billingApi.getPayments(50), []);

  if (payments.loading) {
    return <LoadingSpinner message="Loading payments..." />;
  }

  const paymentList = payments.data?.data ?? [];

  return (
    <PageContainer
      title="Payments"
      description="Top-up payment history"
    >
      <div className="space-y-6">
        {/* Status banner from Stripe redirect */}
        {status === "success" && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4">
            <p className="text-sm font-medium text-green-700">
              Payment successful! Credit will be added to your account shortly.
            </p>
          </div>
        )}
        {status === "cancelled" && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm text-amber-700">
              Payment was cancelled. No charge has been made.
            </p>
          </div>
        )}

        {payments.error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-700">
              Unable to load payments: {payments.error}
            </p>
          </div>
        ) : paymentList.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
            <p className="text-sm text-gray-500">No payments yet</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Description
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Credit Applied
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paymentList.map((payment) => (
                    <tr key={payment.id}>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">
                        {formatDate(payment.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {payment.description}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                        {formatCurrency(payment.amount, payment.currency)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <PaymentStatusBadge status={payment.status} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        {payment.creditAddedToProvider ? (
                          <span className="text-green-600">Yes</span>
                        ) : payment.status === "succeeded" ? (
                          <span className="text-amber-600">Pending</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </PageContainer>
  );
}

function PaymentStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    succeeded: "bg-green-100 text-green-700",
    pending: "bg-amber-100 text-amber-700",
    failed: "bg-red-100 text-red-700",
    refunded: "bg-gray-100 text-gray-600",
  };

  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${styles[status] || "bg-gray-100 text-gray-600"}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}
